import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import { Save, RotateCcw, Eye, AlertTriangle, Shield, FileText, Loader2 } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const LegalDisclaimerManager = () => {
  const [disclaimer, setDisclaimer] = useState({
    title: '',
    content: '',
    short_content: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchDisclaimer();
  }, []);

  const fetchDisclaimer = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/legal-disclaimer`);
      if (response.ok) {
        const data = await response.json();
        setDisclaimer({
          title: data.title || '',
          content: data.content || '',
          short_content: data.short_content || ''
        });
      }
    } catch (error) {
      console.error('Error fetching disclaimer:', error);
      toast({ title: 'Error', description: 'Failed to load disclaimer', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!disclaimer.title.trim() || !disclaimer.content.trim()) {
      toast({ title: 'Validation Error', description: 'Title and content are required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/legal-disclaimer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(disclaimer)
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Legal disclaimer updated successfully' });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error saving disclaimer:', error);
      toast({ title: 'Error', description: 'Failed to save disclaimer', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset to default? This cannot be undone.')) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/legal-disclaimer/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDisclaimer({
          title: data.data.title,
          content: data.data.content,
          short_content: data.data.short_content
        });
        toast({ title: 'Success', description: 'Legal disclaimer reset to default' });
      } else {
        throw new Error('Failed to reset');
      }
    } catch (error) {
      console.error('Error resetting disclaimer:', error);
      toast({ title: 'Error', description: 'Failed to reset disclaimer', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-amber-500" />
            Payment Legal Disclaimer
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage the legal notice shown in booking confirmation emails
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="w-4 h-4 mr-2" />
            {showPreview ? 'Hide' : 'Show'} Preview
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Default
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Info Box */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium">Where this disclaimer appears:</p>
              <ul className="mt-1 space-y-1 list-disc list-inside text-blue-700 dark:text-blue-400">
                <li>Booking confirmation emails (when trainer accepts a booking)</li>
              </ul>
              <p className="mt-2 text-xs text-blue-600 dark:text-blue-500">
                Note: This disclaimer only applies to user-trainer booking transactions, not platform purchases.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Edit Disclaimer
            </CardTitle>
            <CardDescription className="dark:text-gray-400">
              Customize the legal notice text. Use bullet points (•) for list items.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title
              </label>
              <Input
                value={disclaimer.title}
                onChange={(e) => setDisclaimer({ ...disclaimer, title: e.target.value })}
                placeholder="Important Payment Notice"
                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Full Content (for payment pages)
              </label>
              <textarea
                value={disclaimer.content}
                onChange={(e) => setDisclaimer({ ...disclaimer, content: e.target.value })}
                rows={10}
                className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                placeholder="Enter the full disclaimer text...&#10;&#10;• Use bullet points for list items&#10;• Separate paragraphs with blank lines"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Tip: Use • for bullet points, separate paragraphs with blank lines
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Short Version (for emails)
              </label>
              <textarea
                value={disclaimer.short_content}
                onChange={(e) => setDisclaimer({ ...disclaimer, short_content: e.target.value })}
                rows={3}
                className="w-full p-3 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                placeholder="A shorter version for email footers..."
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Used in booking confirmation emails. Keep it concise.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        {showPreview && (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardHeader>
              <CardTitle className="dark:text-white flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Live Preview
              </CardTitle>
              <CardDescription className="dark:text-gray-400">
                How the disclaimer will appear on payment pages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-800/50 rounded-full flex items-center justify-center">
                    <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h4 className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {disclaimer.title || 'Important Payment Notice'}
                  </h4>
                </div>
                
                <div className="space-y-3 text-sm text-amber-800 dark:text-amber-300">
                  {(disclaimer.content || 'Your disclaimer content will appear here...').split('\n\n').map((paragraph, index) => {
                    if (paragraph.includes('•')) {
                      const lines = paragraph.split('\n');
                      return (
                        <ul key={index} className="space-y-2 ml-1">
                          {lines.map((line, lineIndex) => {
                            if (line.startsWith('•')) {
                              return (
                                <li key={lineIndex} className="flex items-start gap-2">
                                  <span className="text-amber-500 mt-1">•</span>
                                  <span>{line.substring(1).trim()}</span>
                                </li>
                              );
                            }
                            return <span key={lineIndex}>{line}</span>;
                          })}
                        </ul>
                      );
                    }
                    return <p key={index}>{paragraph}</p>;
                  })}
                </div>
              </div>

              {/* Email Preview */}
              <div className="mt-6 pt-6 border-t dark:border-gray-700">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Email Version Preview:</p>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      {disclaimer.short_content || disclaimer.content?.substring(0, 200) + '...' || 'Short version will appear here...'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LegalDisclaimerManager;
